/**
 * Hikvision Hik-Partner Pro API Service
 * Handles authentication, site management, device management, and video streaming
 * Based on Hik-Partner Pro OpenAPI V2.15.0
 */

interface HikPartnerConfig {
  apiKey: string;
  secretKey: string;
  baseUrl: string;
}

interface TokenResponse {
  data: {
    accessToken: string;
    expireTime: number;
    areaDomain: string;
  };
  errorCode: string;
}

interface Site {
  id: string;
  name: string;
  address?: string;
  contactName?: string;
  contactPhone?: string;
  latitude?: number;
  longitude?: number;
  createTime?: number;
  updateTime?: number;
}

interface Device {
  id?: string;  // Device ID from API
  deviceSerial: string;
  deviceName: string;
  deviceType: string;
  status: number; // 0: offline, 1: online
  siteId: string;
  siteName?: string;
  channelCount?: number;
  version?: string;
}

interface Channel {
  channelNo: string;
  channelName: string;
  deviceSerial: string;
  status: number; // 0: offline, 1: online
  streamType?: string;
}

class HikPartnerProService {
  private config: HikPartnerConfig;
  private accessToken: string | null = null;
  private tokenExpireTime: number | null = null;
  private areaDomain: string | null = null;

  constructor() {
    // Use local proxy endpoint to avoid CORS issues in development
    // In production, this should be handled by your backend server
    const isDevelopment = process.env.NODE_ENV === 'development';
    const baseUrlConfig = process.env.REACT_APP_HIK_CONNECT_BASE_URL || 'https://ius.hikcentralconnect.com';
    
    // Determine which proxy to use based on configured base URL
    let proxyPath = '/api/hikvision';
    if (baseUrlConfig.includes('tpp.hikvision.com')) {
      proxyPath = '/api/hikvision-tpp';
    }
    
    this.config = {
      apiKey: process.env.REACT_APP_HIK_CONNECT_API_KEY || '',
      secretKey: process.env.REACT_APP_HIK_CONNECT_SECRET_KEY || '',
      // Use local proxy in development, direct API in production
      baseUrl: isDevelopment ? proxyPath : baseUrlConfig,
    };
  }

  /**
   * Check if Partner Pro integration is enabled and configured
   */
  isEnabled(): boolean {
    const enabled = process.env.REACT_APP_HIK_CONNECT_ENABLED === 'true';
    const configured = !!(this.config.apiKey && this.config.secretKey);
    return enabled && configured;
  }

  /**
   * Get access token, refreshing if expired
   */
  private async getAccessToken(): Promise<string> {
    // Check if token is still valid (with 5 minute buffer)
    const now = Date.now();
    const bufferTime = 5 * 60 * 1000; // 5 minutes

    if (this.accessToken && this.tokenExpireTime && this.tokenExpireTime > now + bufferTime) {
      return this.accessToken;
    }

    // Get access token from HikCentral Connect
    const response = await fetch(`${this.config.baseUrl}/api/hccgw/platform/v1/token/get`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        appKey: this.config.apiKey,
        secretKey: this.config.secretKey,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get access token: ${response.statusText}`);
    }

    const data: TokenResponse = await response.json();

    if (data.errorCode !== '0') {
      // Provide helpful error messages for common error codes
      const errorMessages: Record<string, string> = {
        'LAP300001': 'API Key not found. Please verify your REACT_APP_HIK_CONNECT_API_KEY is correct and activated.',
        'LAP300002': 'API Key and Secret Key mismatch. Please verify both credentials are correct.',
        'LAP300003': 'Hik-Partner Pro account does not exist. Please check your account status.',
        'LAP500004': 'Token expired. Will request new token automatically.',
      };
      
      const errorMsg = errorMessages[data.errorCode] || `Unknown error: ${data.errorCode}`;
      throw new Error(`Authentication failed (${data.errorCode}): ${errorMsg}`);
    }

    this.accessToken = data.data.accessToken;
    this.tokenExpireTime = data.data.expireTime;
    this.areaDomain = data.data.areaDomain;

    console.log('[Hikvision] Authentication successful');
    console.log('[Hikvision] Area Domain:', this.areaDomain);
    console.log('[Hikvision] Token expires:', new Date(this.tokenExpireTime));

    return this.accessToken;
  }

  /**
   * Get the appropriate base URL for API requests
   * Maps areaDomain to local proxy in development
   */
  private getApiBaseUrl(): string {
    const isDevelopment = process.env.NODE_ENV === 'development';
    
    if (!isDevelopment) {
      // Production: use areaDomain if available, otherwise use config baseUrl
      return this.areaDomain || this.config.baseUrl;
    }
    
    // Development: map areaDomain to appropriate proxy
    if (this.areaDomain) {
      // HikCentral Connect area domains
      if (this.areaDomain.includes('hikcentralconnect.com')) {
        return '/api/hikvision-area';
      }
      // Legacy Hik-Partner Pro area domains
      else if (this.areaDomain.includes('isgpapi')) {
        return '/api/hikvision-area';
      } else if (this.areaDomain.includes('tpp')) {
        return '/api/hikvision-tpp';
      } else if (this.areaDomain.includes('api.hik-partner.com')) {
        return '/api/hikvision';
      }
    }
    
    // Fallback to original proxy
    return this.config.baseUrl;
  }

  /**
   * Make authenticated API request
   * Uses token from getAccessToken() for all requests
   * Automatically retries once if token is expired (LAP500004)
   */
  private async request<T>(endpoint: string, options: RequestInit = {}, retryCount = 0): Promise<T> {
    // Step 1: Get token (cached if still valid)
    const token = await this.getAccessToken();
    const baseUrl = this.getApiBaseUrl();

    console.log(`[Hikvision] API Request: ${endpoint}`);
    console.log(`[Hikvision] Using token: ${token.substring(0, 20)}...`);

    // Step 2: Use token in Token header for HikConnect for Teams
    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Token': token,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    const data = await response.json();

    if (data.errorCode && data.errorCode !== '0') {
      // If token expired (LAP500004), clear token and retry once
      if (data.errorCode === 'LAP500004' && retryCount === 0) {
        console.log('[Hikvision] Token expired, refreshing...');
        this.accessToken = null;
        this.tokenExpireTime = null;
        return this.request<T>(endpoint, options, retryCount + 1);
      }
      
      // Provide better error messages
      const errorMessages: Record<string, string> = {
        'LAP300001': 'API Key not found or not activated',
        'LAP300002': 'API Key and Secret Key mismatch',
        'LAP300003': 'Account does not exist',
        'LAP500004': 'Token expired (retry failed)',
      };
      
      const errorMsg = errorMessages[data.errorCode] || data.errorMsg || 'Unknown error';
      throw new Error(`${errorMsg} (${data.errorCode})`);
    }

    return data;
  }

  /**
   * Search for sites/locations (Areas in HikConnect)
   */
  async getSites(params?: {
    pageNo?: number;
    pageSize?: number;
    siteName?: string;
  }): Promise<{ data: { list: Site[]; total: number } }> {
    const response = await this.request<any>('/api/hccgw/resource/v1/areas/get', {
      method: 'POST',
      body: JSON.stringify({
        pageIndex: params?.pageNo || 1,
        pageSize: Math.min(params?.pageSize || 100, 500),
        filter: {
          parentAreaID: '-1',
          includeSubArea: 1
        }
      }),
    });
    
    // Transform response to match expected format
    return {
      data: {
        list: response.data.area?.map((area: any) => ({
          id: area.id,
          name: area.name,
          parentId: area.parentAreaID,
          type: area.type
        })) || [],
        total: response.data.totalCount || 0
      }
    };
  }

  /**
   * Get devices for a specific site (area)
   */
  async getDevices(siteId: string): Promise<{ data: { list: Device[] } }> {
    const response = await this.request<any>('/api/hccgw/resource/v1/devices/get', {
      method: 'POST',
      body: JSON.stringify({
        pageIndex: 1,
        pageSize: 500,
        areaId: siteId,
        deviceCategory: 'encodingDevice'
      }),
    });
    
    // Transform response to match expected format
    // onlineStatus: 1 = online, 0 = offline (numeric)
    return {
      data: {
        list: response.data.device?.map((device: any) => ({
          id: device.id,
          deviceSerial: device.serialNo,
          deviceName: device.name,
          deviceType: device.type,
          status: parseInt(device.onlineStatus) || 0,  // Ensure numeric: 0 = offline, 1 = online
          siteId: siteId,
          channelCount: device.channelCount,
          version: device.version
        })) || []
      }
    };
  }

  /**
   * Get all devices across all sites
   */
  async getAllDevices(): Promise<Device[]> {
    const sitesResponse = await this.getSites({ pageSize: 1000 });
    const sites = sitesResponse.data.list;

    const allDevices: Device[] = [];

    for (const site of sites) {
      try {
        const devicesResponse = await this.getDevices(site.id);
        const devices = devicesResponse.data.list.map(device => ({
          ...device,
          siteName: site.name,
        }));
        allDevices.push(...devices);
      } catch (error) {
        console.error(`Failed to fetch devices for site ${site.name}:`, error);
      }
    }

    return allDevices;
  }

  /**
   * Get channels (cameras) for a specific device
   */
  async getDeviceChannels(deviceSerial: string): Promise<{ data: { list: Channel[] } }> {
    const response = await this.request<any>('/api/hccgw/resource/v1/areas/cameras/get', {
      method: 'POST',
      body: JSON.stringify({
        pageIndex: 1,
        pageSize: 500,
        filter: {
          areaID: '-1',
          deviceSerialNo: deviceSerial,
          includeSubArea: '0'
        }
      }),
    });
    
    // Transform response to match expected format
    // Note: camera.online is a STRING "1" or "0", not a number
    return {
      data: {
        list: response.data.camera?.map((camera: any) => ({
          channelNo: camera.device.channelInfo.no,
          channelName: camera.name,
          deviceSerial: deviceSerial,
          resourceId: camera.id,
          status: parseInt(camera.online) || 0  // Convert string to number: 0 = offline, 1 = online
        })) || []
      }
    };
  }

  /**
   * Get all cameras across all devices
   */
  async getAllCameras(): Promise<Array<Channel & { siteName?: string; deviceName?: string }>> {
    const devices = await this.getAllDevices();
    const allCameras: Array<Channel & { siteName?: string; deviceName?: string }> = [];

    for (const device of devices) {
      try {
        const channelsResponse = await this.getDeviceChannels(device.deviceSerial);
        const channels = channelsResponse.data.list.map(channel => ({
          ...channel,
          siteName: device.siteName,
          deviceName: device.deviceName,
        }));
        allCameras.push(...channels);
      } catch (error) {
        console.error(`Failed to fetch channels for device ${device.deviceName}:`, error);
      }
    }

    return allCameras;
  }

  /**
   * Subscribe to events/alarms
   */
  async subscribeToEvents(eventTypes: string[]): Promise<any> {
    return this.request('/api/hpcgw/v1/mq/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        eventTypes,
      }),
    });
  }

  /**
   * Get event messages
   */
  async getEventMessages(params?: { offset?: string; count?: number }): Promise<any> {
    return this.request('/api/hpcgw/v1/mq/messages', {
      method: 'POST',
      body: JSON.stringify({
        offset: params?.offset,
        count: params?.count || 100,
      }),
    });
  }

  /**
   * Add a new site
   */
  async addSite(siteData: {
    name: string;
    address?: string;
    contactName?: string;
    contactPhone?: string;
    latitude?: number;
    longitude?: number;
  }): Promise<{ data: { id: string } }> {
    return this.request('/api/hpcgw/v1/site/add', {
      method: 'POST',
      body: JSON.stringify(siteData),
    });
  }

  /**
   * Update site information
   */
  async updateSite(
    siteId: string,
    siteData: {
      name?: string;
      address?: string;
      contactName?: string;
      contactPhone?: string;
      latitude?: number;
      longitude?: number;
    }
  ): Promise<any> {
    return this.request(`/api/hpcgw/v1/site/${siteId}/update`, {
      method: 'POST',
      body: JSON.stringify(siteData),
    });
  }

  /**
   * Delete a site
   */
  async deleteSite(siteId: string): Promise<any> {
    return this.request(`/api/hpcgw/v1/site/${siteId}/delete`, {
      method: 'POST',
    });
  }
}

// Export class and singleton instance
export { HikPartnerProService };
export const hikPartnerProService = new HikPartnerProService();
export type { Site, Device, Channel };
